// Pure derivation functions extracted from usePipelineState's useMemo blocks.
//
// Each function is a pure transformation of its inputs — no React, no hooks,
// no side effects. The hook calls these inside its useMemo callbacks so the
// memoization semantics (and hook call order) are preserved exactly.
//
// Pipeline-state derivation functions (computeEffectivePipelineState,
// computeGlobalFilteredPipelineState, computePipelineStatus, computeBuildFailed,
// computeAllCompleted, deriveStages, deriveStageStatusFromRows) live in
// ./pipeline-state-derive-helpers.ts.

import type { StackTemplate } from "@domains/application/types";
import type { DeploymentEvent } from "src/domains/application/hooks/useDeploymentEvents";
import type { PipelineStep as PipelineStateStep } from "src/domains/application/hooks/useDeploymentStream";
import type { PipelineRunRow } from "src/domains/application/hooks/usePipelineRunsApi";

import { type TemplateServiceInfo } from "../pipeline-tab-helpers";

import { SYNTHETIC_STATUS_MESSAGES } from "./pipeline-state-constants";

// ── Latest saga selection ────────────────────────────────────────────────────

/**
 * Map a raw StackTemplate's services array into TemplateServiceInfo objects.
 * Pure transformation used by the template-services fetch effect.
 */
export function mapTemplateServices(
  tmpl: StackTemplate,
): TemplateServiceInfo[] {
  return (tmpl.services as unknown as Array<Record<string, unknown>>).map(
    (s) => ({
      name: s.name as string,
      type: s.type as string,
      baseImage: (s.vcs as Record<string, unknown>)?.baseImage as
        string | undefined,
      buildCommand: (s.vcs as Record<string, unknown>)?.buildCommand as
        string | undefined,
      dockerfile: (s.vcs as Record<string, unknown>)?.dockerfile as
        string | undefined,
    }),
  );
}

/**
 * Merge assembled-manifest services (including observability-injected ones
 * like prometheus/grafana) into the existing template services list, so the
 * DAG group nodes show all services the stack actually has. Pure transformation.
 */
export function mergeManifestServices(
  prev: TemplateServiceInfo[] | undefined,
  services: Array<{ name: string; type: string }>,
): TemplateServiceInfo[] {
  const existing = new Map((prev ?? []).map((s) => [s.name, s]));
  for (const svc of services) {
    if (!existing.has(svc.name)) {
      existing.set(svc.name, { name: svc.name, type: svc.type });
    }
  }
  return Array.from(existing.values());
}

// ── Latest saga determination ────────────────────────────────────────────────

/**
 * Determine the latest sagaId from per-service pipeline rows.
 *
 * Phase-level rows (serviceName === "") have artificially fresh updatedAt
 * timestamps from retry/scheduling events and are excluded — otherwise a
 * stale build retry's phase row could dominate selection, hiding deploy rows
 * from a different sagaId. The `vcs` step is excluded from this exclusion
 * because its rows are not phase-level in the same way.
 */
export function deriveLatestSagaId(
  pipelineState: PipelineStateStep[] | undefined,
): string | null {
  if (!pipelineState || pipelineState.length === 0) return null;
  const serviceRows = pipelineState.filter(
    (s) => s.serviceName !== "" || s.step === "vcs",
  );
  if (serviceRows.length === 0) return null;
  return serviceRows.reduce((latest, s) =>
    s.updatedAt > latest.updatedAt ? s : latest,
  ).sagaId;
}

// ── Log batch scoping ────────────────────────────────────────────────────────

export function getScopedLogBatches(
  logBatches: Map<string, Map<string, Map<string, string[]>>>,
  targetSaga: string | null,
): Map<string, Map<string, string[]>> {
  if (!targetSaga) return new Map<string, Map<string, string[]>>();
  return logBatches.get(targetSaga) ?? new Map<string, Map<string, string[]>>();
}

// ── Pipeline state scoping ───────────────────────────────────────────────────

export function scopePipelineState(
  pipelineState: PipelineStateStep[],
  targetSaga: string | null,
): PipelineStateStep[] {
  if (pipelineState.length === 0) return pipelineState;
  if (!targetSaga) return pipelineState;
  return pipelineState.filter((s) => s.sagaId === targetSaga);
}

// ── Phase row filtering for DAG ──────────────────────────────────────────────

/**
 * Filter out phase-level rows (serviceName === "") for build/deploy/vcs steps,
 * EXCEPT when they carry a "failed" status. Phase-level "failed" rows are
 * produced by propagateFailureStatus() and must reach usePipelineGraphFromState
 * so mergeWithPhaseStatus can turn the group node border red.
 */
export function filterPhaseRowsForDag(
  scopedPipelineState: PipelineStateStep[],
): PipelineStateStep[] {
  if (scopedPipelineState.length === 0) return scopedPipelineState;
  return scopedPipelineState.filter(
    (s) =>
      !(
        s.serviceName === "" &&
        (s.step === "build" || s.step === "deploy" || s.step === "vcs") &&
        s.status !== "failed"
      ),
  );
}

// ── Event filtering ──────────────────────────────────────────────────────────

export function filterEventsBySaga(
  events: DeploymentEvent[],
  selectedRunId: string | null,
): DeploymentEvent[] {
  if (!selectedRunId) return events;
  return events.filter((e) => e.sagaId === selectedRunId);
}

// ── Selected API run lookup ──────────────────────────────────────────────────

export function findSelectedApiRun(
  selectedRunId: string | null,
  apiRuns: PipelineRunRow[] | undefined,
): PipelineRunRow | null {
  if (!selectedRunId || !apiRuns) return null;
  return apiRuns.find((r) => r.sagaId === selectedRunId) ?? null;
}

// ── Synthetic pending state ──────────────────────────────────────────────────

export interface SyntheticPendingStateParams {
  selectedRunId: string | null;
  selectedApiRun: PipelineRunRow | null;
  filteredPipelineState: PipelineStateStep[];
  templateServices: TemplateServiceInfo[] | undefined;
  alias: string;
  pipelineState: PipelineStateStep[];
}

/**
 * Generate synthetic "pending" PipelineStep rows when a run is selected
 * (e.g. a freshly triggered "queued" run) but filteredPipelineState is empty
 * (no real rows for that sagaId yet). This keeps the DAG showing placeholder
 * nodes instead of falling back to stale event-based state.
 */
export function buildSyntheticPendingState({
  selectedRunId,
  selectedApiRun,
  filteredPipelineState,
  templateServices,
  alias,
  pipelineState,
}: SyntheticPendingStateParams): PipelineStateStep[] {
  if (
    !selectedRunId ||
    !selectedApiRun ||
    (selectedApiRun.status !== "queued" &&
      selectedApiRun.status !== "in_progress") ||
    filteredPipelineState.length > 0
  ) {
    return [];
  }

  const now = new Date().toISOString();
  const services = templateServices ?? [{ name: "ui", type: "server" }];
  const buildable = services.filter((s) => s.type !== "stateful");
  const stateful = services.filter((s) => s.type === "stateful");

  // Check phase-level rows (serviceName === "") for this saga to determine
  // build/deploy status. When the deploy-service only creates phase-level
  // events (not per-service), these rows carry the actual status.
  const buildPhaseRow = pipelineState.find(
    (s) =>
      s.sagaId === selectedRunId && s.step === "build" && s.serviceName === "",
  );
  const deployPhaseRow = pipelineState.find(
    (s) =>
      s.sagaId === selectedRunId && s.step === "deploy" && s.serviceName === "",
  );

  const buildSyntheticStatus = buildPhaseRow?.status ?? "pending";
  const buildSyntheticMessage =
    SYNTHETIC_STATUS_MESSAGES[buildSyntheticStatus] ?? "Queued";

  const deploySyntheticStatus = deployPhaseRow?.status ?? "pending";
  const deploySyntheticMessage =
    SYNTHETIC_STATUS_MESSAGES[deploySyntheticStatus] ?? "Queued";

  const steps: PipelineStateStep[] = [];

  // Build steps for buildable services
  for (const svc of buildable) {
    steps.push({
      id: 0,
      systemId: 0,
      systemAlias: alias,
      step: "build",
      serviceName: svc.name,
      status: buildSyntheticStatus,
      severity: "info",
      message: buildSyntheticMessage,
      eventType: "synthetic",
      sagaId: selectedRunId,
      startedAt: null,
      completedAt: null,
      updatedAt: selectedApiRun.createdAt || now,
    });
  }

  // Deploy steps for all services
  for (const svc of [...buildable, ...stateful]) {
    steps.push({
      id: 0,
      systemId: 0,
      systemAlias: alias,
      step: "deploy",
      serviceName: svc.name,
      status: deploySyntheticStatus,
      severity: "info",
      message: deploySyntheticMessage,
      eventType: "synthetic",
      sagaId: selectedRunId,
      startedAt: null,
      completedAt: null,
      updatedAt: selectedApiRun.createdAt || now,
    });
  }

  return steps;
}
